import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, Calendar, List, Check, Filter, X, RotateCcw, Save, Upload, Download, Plus, Edit2, Trash2, Copy } from 'lucide-react';

// Task Templates
const taskTemplates = [
  {
    id: 'weekly-cleaning',
    name: "Weekly Cleaning",
    emoji: "🏠",
    tasks: [
      { name: "Vacuum living room", emoji: "🔌", frequency: "weekly", interval: 1, preferredDay: 5 },
      { name: "Clean bathroom", emoji: "🚿", frequency: "weekly", interval: 1, preferredDay: 4 },
      { name: "Clean kitchen", emoji: "🍳", frequency: "daily", interval: 1 },
      { name: "Mop floors", emoji: "🧹", frequency: "weekly", interval: 1, preferredDay: 6 },
      { name: "Dust furniture", emoji: "🪶", frequency: "weekly", interval: 1, preferredDay: 3 }
    ]
  },
  {
    id: 'monthly-maintenance',
    name: "Monthly Maintenance",
    emoji: "🔧",
    tasks: [
      { name: "Check air filters", emoji: "💨", frequency: "monthly", interval: 1, preferredDay: 0 },
      { name: "Deep clean appliances", emoji: "✨", frequency: "monthly", interval: 1, preferredDay: 0 },
      { name: "Organize closets", emoji: "👕", frequency: "monthly", interval: 1, preferredDay: 6 },
      { name: "Clean windows", emoji: "🪟", frequency: "monthly", interval: 1, preferredDay: 6 }
    ]
  },
  {
    id: 'biweekly-tasks',
    name: "Bi-weekly Tasks",
    emoji: "📅",
    tasks: [
      { name: "Change bedsheets", emoji: "🛏️", frequency: "weekly", interval: 2, preferredDay: 0 },
      { name: "Water plants", emoji: "🪴", frequency: "weekly", interval: 2, preferredDay: 3 },
      { name: "Clean car", emoji: "🚗", frequency: "weekly", interval: 2, preferredDay: 6 }
    ]
  },
  {
    id: 'daily-routine',
    name: "Daily Routine",
    emoji: "☀️",
    tasks: [
      { name: "Make bed", emoji: "🛏️", frequency: "daily", interval: 1 },
      { name: "Dishes", emoji: "🍽️", frequency: "daily", interval: 1 },
      { name: "Tidy up", emoji: "🧹", frequency: "daily", interval: 1 },
      { name: "Take out trash", emoji: "🗑️", frequency: "daily", interval: 2 }
    ]
  }
];

// Task Badge Component with Emoji
const TaskBadge = ({ task, person, isCompleted, onToggle, onDragStart, onDragEnd, isDraggable }) => {
  return (
    <div
      draggable={isDraggable}
      onDragStart={isDraggable ? onDragStart : undefined}
      onDragEnd={onDragEnd}
      onClick={onToggle}
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium w-full 
        cursor-pointer select-none transition-all duration-300 ease-out 
        ${isDraggable && !isCompleted ? 'hover:scale-105 hover:-translate-y-0.5 active:scale-100' : ''} 
        ${isCompleted
          ? 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-400 border border-gray-300'
          : 'text-white shadow-md hover:shadow-lg border border-white/20 backdrop-blur-sm'
        } 
      `}
      style={{
        background: isCompleted
          ? undefined
          : `linear-gradient(135deg, ${person.color}ee, ${person.color}cc)`,
        textDecoration: isCompleted ? 'line-through' : 'none',
        textDecorationThickness: isCompleted ? '2px' : undefined,
        textDecorationColor: isCompleted ? '#9ca3af' : undefined
      }}
    >
      {task.emoji && (
        <span className={`text-sm ${isCompleted ? 'opacity-50' : ''}`}>
          {task.emoji}
        </span>
      )}
      {isCompleted ? (
        <div className="w-4 h-4 rounded-full bg-gray-400 flex items-center justify-center">
          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
        </div>
      ) : (
        <div
          className="w-2 h-2 rounded-full bg-white/30 ring-1 ring-white/40"
          style={{ boxShadow: '0 0 4px rgba(255,255,255,0.3)' }}
        />
      )}
      <span className={`${isCompleted ? 'opacity-70' : 'font-medium tracking-wide'} truncate`}>
        {task.name}
      </span>
    </div>
  );
};

// Task Form Component for Add/Edit
const TaskForm = ({ task, persons, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: task?.name || '',
    emoji: task?.emoji || '📌',
    personId: task?.personId || 1,
    frequency: task?.frequency || 'weekly',
    interval: task?.interval || 1,
    preferredDay: task?.preferredDay ?? 1
  });
  const commonEmojis = ['📌', '🧹', '🍽️', '🗑️', '🧺', '💨', '🚿', '🍳', '🛏️', '🪴', '🔌', '🛒', '✨', '🥘', '📬', '🚗', '🪟', '👕'];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Please enter a task name');
      return;
    }
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Task Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter task name..."
            autoFocus
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Emoji</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={formData.emoji}
              onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
              className="w-20 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
              maxLength="2"
            />
            <div className="flex-1 flex flex-wrap gap-1">
              {commonEmojis.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => setFormData({ ...formData, emoji })}
                  className={`p-1 rounded hover:bg-gray-100 ${formData.emoji === emoji ? 'bg-gray-200' : ''}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
          <select
            value={formData.personId}
            onChange={(e) => setFormData({ ...formData, personId: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {persons.map(person => (
              <option key={person.id} value={person.id}>{person.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
          <select
            value={formData.frequency}
            onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Interval</label>
          <select
            value={formData.interval}
            onChange={(e) => setFormData({ ...formData, interval: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="1">Every</option>
            <option value="2">Every 2</option>
            <option value="3">Every 3</option>
            <option value="4">Every 4</option>
          </select>
        </div>
      </div>

      {(formData.frequency === 'weekly' || formData.frequency === 'monthly') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Day</label>
          <select
            value={formData.preferredDay}
            onChange={(e) => setFormData({ ...formData, preferredDay: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="0">Sunday</option>
            <option value="1">Monday</option>
            <option value="2">Tuesday</option>
            <option value="3">Wednesday</option>
            <option value="4">Thursday</option>
            <option value="5">Friday</option>
            <option value="6">Saturday</option>
          </select>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          {task ? 'Update' : 'Add'} Task
        </button>
      </div>
    </form>
  );
};

// Template Preview Component
const TemplatePreview = ({ template, persons, existingTasks, onApply, onBack }) => {
  const [selectedTasks, setSelectedTasks] = useState(
    template.tasks.map(t => ({ ...t, selected: true, personId: 1 }))
  );

  const toggleTask = (index) => {
    const updated = [...selectedTasks];
    updated[index].selected = !updated[index].selected;
    setSelectedTasks(updated);
  };

  const updateTaskPerson = (index, personId) => {
    const updated = [...selectedTasks];
    updated[index].personId = parseInt(personId);
    setSelectedTasks(updated);
  };

  const handleApply = () => {
    const tasksToAdd = selectedTasks.filter(t => t.selected);
    if (tasksToAdd.length === 0) {
      alert('Please select at least one task to add');
      return;
    }
    onApply(tasksToAdd);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-semibold flex items-center gap-2">
            <span className="text-2xl">{template.emoji}</span>
            {template.name}
          </h3>
          <p className="text-sm text-gray-600 mt-1">Select tasks to add and assign them to people</p>
        </div>
        <button
          onClick={onBack}
          className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          ← Back
        </button>
      </div>

      <div className="space-y-2 mb-6">
        {selectedTasks.map((task, index) => {
          const exists = existingTasks.some(t =>
            t.name.toLowerCase().trim() === task.name.toLowerCase().trim()
          );

          return (
            <div
              key={index}
              className={`p-3 rounded-lg border ${exists ? 'bg-gray-50 border-gray-300' :
                  task.selected ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'
                }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <input
                    type="checkbox"
                    checked={task.selected && !exists}
                    onChange={() => !exists && toggleTask(index)}
                    disabled={exists}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50"
                  />
                  <span className="text-lg">{task.emoji}</span>
                  <div className="flex-1">
                    <span className={`font-medium ${exists ? 'text-gray-400 line-through' : ''}`}>
                      {task.name}
                    </span>
                    {exists && <span className="text-xs text-gray-500 ml-2">(Already exists)</span>}
                    <div className="text-xs text-gray-500 mt-1">
                      {task.frequency === 'daily' && `Every ${task.interval === 1 ? 'day' : `${task.interval} days`}`}
                      {task.frequency === 'weekly' && `Every ${task.interval === 1 ? 'week' : `${task.interval} weeks`}`}
                      {task.frequency === 'monthly' && 'Monthly'}
                      {task.preferredDay !== null && task.preferredDay !== undefined &&
                        ` on ${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][task.preferredDay]}`
                      }
                    </div>
                  </div>
                </div>
                {!exists && (
                  <select
                    value={task.personId}
                    onChange={(e) => updateTaskPerson(index, e.target.value)}
                    disabled={!task.selected}
                    className="px-3 py-1 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {persons.map(person => (
                      <option key={person.id} value={person.id}>{person.name}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between items-center pt-4 border-t">
        <div className="text-sm text-gray-600">
          {selectedTasks.filter(t => t.selected && !existingTasks.some(e =>
            e.name.toLowerCase().trim() === t.name.toLowerCase().trim()
          )).length} of {template.tasks.length} tasks will be added
        </div>
        <button
          onClick={handleApply}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
        >
          Add Selected Tasks
        </button>
      </div>
    </div>
  );
};

// Main Todo Manager Component
const TodoManager = () => {
  // Initial state
  const [view, setView] = useState('weekly');
  const [weeklyMode, setWeeklyMode] = useState('calendar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [filter, setFilter] = useState({ person: 'all', status: 'all' });
  const [showFilter, setShowFilter] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showPatternManager, setShowPatternManager] = useState(false);
  const [currentPattern, setCurrentPattern] = useState(null);
  const [patterns, setPatterns] = useState({});

  // Tasks with emojis
  const [tasks, setTasks] = useState([
    { id: 1, name: "Clean kitchen", emoji: "🍳", personId: 1, frequency: "daily", interval: 1, preferredDay: null },
    { id: 2, name: "Water plants", emoji: "🪴", personId: 2, frequency: "weekly", interval: 2, preferredDay: 3 },
    { id: 3, name: "Vacuum living room", emoji: "🔌", personId: 1, frequency: "weekly", interval: 1, preferredDay: 5 },
    { id: 4, name: "Grocery shopping", emoji: "🛒", personId: 2, frequency: "weekly", interval: 1, preferredDay: 6 },
    { id: 5, name: "Deep cleaning", emoji: "✨", personId: 1, frequency: "monthly", interval: 1, preferredDay: 0 },
    { id: 6, name: "Change bedsheets", emoji: "🛏️", personId: 2, frequency: "weekly", interval: 2, preferredDay: 0 },
    { id: 7, name: "Clean bathroom", emoji: "🚿", personId: 1, frequency: "weekly", interval: 1, preferredDay: 4 },
    { id: 8, name: "Meal prep", emoji: "🥘", personId: 2, frequency: "weekly", interval: 1, preferredDay: 0 },
    { id: 9, name: "Take out trash", emoji: "🗑️", personId: 2, frequency: "daily", interval: 2, preferredDay: null },
    { id: 10, name: "Check mail", emoji: "📬", personId: 1, frequency: "daily", interval: 1, preferredDay: null }
  ]);
  const [persons] = useState([
    { id: 1, name: "Person A", color: "#9333EA" },
    { id: 2, name: "Person B", color: "#10B981" }
  ]);

  // Two-tier assignment system
  // Primary: Default task assignments based on rules
  const [defaultAssignments, setDefaultAssignments] = useState({});
  // Secondary: 4-week pattern that overrides defaults when present
  const [fourWeekPattern, setFourWeekPattern] = useState(() => {
    try {
      const saved = localStorage.getItem('fourWeekPattern');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Current task assignments (computed from pattern or defaults)
  const [taskAssignments, setTaskAssignments] = useState({});

  // Save patterns to localStorage
  useEffect(() => {
    if (fourWeekPattern) {
      localStorage.setItem('fourWeekPattern', JSON.stringify(fourWeekPattern));
    }
  }, [fourWeekPattern]);

  // Get week number in 4-week cycle
  const getWeekInCycle = (date) => {
    const referenceDate = new Date('2025-01-06'); // A Monday as reference
    const daysDiff = Math.floor((date - referenceDate) / (24 * 60 * 60 * 1000));
    const weeksDiff = Math.floor(daysDiff / 7);
    return weeksDiff % 4; // 0-3 for 4-week cycle
  };

  // Check if task should be scheduled on date (for default assignments)
  const shouldScheduleTask = (task, date) => {
    const dayOfWeek = date.getDay();

    if (task.frequency === 'daily') {
      if (task.interval === 1) {
        return true;
      } else {
        const referenceDate = new Date('2025-01-01');
        const daysDiff = Math.floor((date - referenceDate) / (24 * 60 * 60 * 1000));
        return daysDiff % task.interval === 0;
      }
    }

    if (task.frequency === 'weekly') {
      if (dayOfWeek !== task.preferredDay) return false;

      if (task.interval > 1) {
        const referenceDate = new Date('2025-01-06');
        const weeksDiff = Math.floor((date - referenceDate) / (7 * 24 * 60 * 60 * 1000));
        return weeksDiff % task.interval === 0;
      }
      return true;
    } else if (task.frequency === 'monthly') {
      if (dayOfWeek === task.preferredDay && date.getDate() <= 7) {
        if (task.interval > 1) {
          const referenceDate = new Date('2025-01-01');
          const monthsDiff = (date.getFullYear() - referenceDate.getFullYear()) * 12 +
            (date.getMonth() - referenceDate.getMonth());
          return monthsDiff % task.interval === 0;
        }
        return true;
      }
    }
    return false;
  };

  // Initialize or update assignments
  useEffect(() => {
    const startDate = new Date(currentDate);
    startDate.setMonth(startDate.getMonth() - 1);
    startDate.setDate(1);

    const endDate = new Date(currentDate);
    endDate.setMonth(endDate.getMonth() + 2);
    endDate.setDate(0);

    const newAssignments = {};

    const current = new Date(startDate);
    while (current <= endDate) {
      const dateStr = current.toISOString().split('T')[0];
      const weekInCycle = getWeekInCycle(current);
      const dayOfWeek = current.getDay();

      newAssignments[dateStr] = {};

      // Check if we have a pattern for this week
      if (fourWeekPattern && fourWeekPattern[weekInCycle]) {
        const weekPattern = fourWeekPattern[weekInCycle];
        const dayPattern = weekPattern[dayOfWeek];

        if (dayPattern) {
          // Use pattern assignments
          Object.entries(dayPattern).forEach(([taskId, status]) => {
            const task = tasks.find(t => t.id === parseInt(taskId));
            if (task) {
              // Only apply pattern if task matches the week type
              if (task.frequency === 'daily' ||
                (task.frequency === 'weekly' && shouldScheduleTask(task, current)) ||
                (task.frequency === 'monthly' && shouldScheduleTask(task, current))) {
                newAssignments[dateStr][taskId] = status;
              }
            }
          });
        }
      }

      // If no pattern, use default scheduling
      if (Object.keys(newAssignments[dateStr]).length === 0) {
        tasks.forEach(task => {
          if (shouldScheduleTask(task, current)) {
            newAssignments[dateStr][task.id] = { completed: false };
          }
        });
      }

      current.setDate(current.getDate() + 1);
    }

    setTaskAssignments(newAssignments);
  }, [currentDate, view, tasks, fourWeekPattern]);

  // Get tasks for a specific date
  const getTasksForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const dayAssignments = taskAssignments[dateStr] || {};
    const result = [];

    Object.entries(dayAssignments).forEach(([taskId, status]) => {
      const task = tasks.find(t => t.id === parseInt(taskId));
      if (!task) return;

      const person = persons.find(p => p.id === task.personId);
      if (!person) return;

      if (filter.person !== 'all' && task.personId !== parseInt(filter.person)) return;
      if (filter.status === 'completed' && !status.completed) return;
      if (filter.status === 'pending' && status.completed) return;

      result.push({
        ...task,
        person,
        isCompleted: status.completed || false
      });
    });

    return result;
  };

  // Toggle task completion
  const toggleTaskCompletion = (taskId, dateStr) => {
    setTaskAssignments(prev => {
      const updated = JSON.parse(JSON.stringify(prev));

      if (!updated[dateStr]) {
        updated[dateStr] = {};
      }

      if (updated[dateStr][taskId]) {
        updated[dateStr][taskId] = {
          ...updated[dateStr][taskId],
          completed: !updated[dateStr][taskId].completed
        };
      } else {
        updated[dateStr][taskId] = { completed: true };
      }

      return updated;
    });
  };

  // Check if date is valid drop target based on task frequency
  const isValidDropTarget = (task, targetDate, fromDate) => {
    if (!task) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Can't move to past dates
    if (targetDate < today) return false;

    const fromDateObj = new Date(fromDate);
    const targetWeekStart = getStartOfWeek(targetDate);
    const fromWeekStart = getStartOfWeek(fromDateObj);
    const targetMonth = targetDate.getMonth();
    const fromMonth = fromDateObj.getMonth();

    // Constraint based on frequency
    if (task.frequency === 'weekly') {
      // Weekly tasks must stay within the same week
      return targetWeekStart.getTime() === fromWeekStart.getTime();
    } else if (task.frequency === 'monthly') {
      // Monthly tasks must stay within the same month
      return targetMonth === fromMonth && targetDate.getFullYear() === fromDateObj.getFullYear();
    }

    // Daily tasks can move anywhere in the future
    return true;
  };

  // Drag and drop handlers
  const handleDragStart = (e, task, fromDate) => {
    setDraggedItem({ task, fromDate });
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverDate(null);
  };
  const handleDragOver = (e, date) => {
    e.preventDefault();
    const dateStr = date.toISOString().split('T')[0];

    if (draggedItem && isValidDropTarget(draggedItem.task, date, draggedItem.fromDate)) {
      setDragOverDate(dateStr);
      e.dataTransfer.dropEffect = 'move';
    } else if (draggedItem) {
      setDragOverDate(null);
      e.dataTransfer.dropEffect = 'none';
    }
  };
  const handleDragLeave = () => {
    setDragOverDate(null);
  };
  const handleDrop = (e, date) => {
    e.preventDefault();

    if (!draggedItem || !isValidDropTarget(draggedItem.task, date, draggedItem.fromDate)) {
      setDraggedItem(null);
      setDragOverDate(null);
      return;
    }

    const toDateStr = date.toISOString().split('T')[0];
    const { task, fromDate } = draggedItem;

    if (fromDate === toDateStr) {
      setDraggedItem(null);
      setDragOverDate(null);
      return;
    }

    setTaskAssignments(prev => {
      const updated = { ...prev };

      const taskStatus = updated[fromDate]?.[task.id] || { completed: false };

      if (updated[fromDate] && updated[fromDate][task.id]) {
        delete updated[fromDate][task.id];
        if (Object.keys(updated[fromDate]).length === 0) {
          delete updated[fromDate];
        }
      }

      if (!updated[toDateStr]) {
        updated[toDateStr] = {};
      }
      updated[toDateStr][task.id] = taskStatus;

      return updated;
    });

    setDraggedItem(null);
    setDragOverDate(null);
  };

  // Save current 4-week pattern
  const saveCurrentPattern = () => {
    const pattern = {};
    const startDate = getStartOfWeek(currentDate);

    // Capture 4 weeks of data
    for (let week = 0; week < 4; week++) {
      pattern[week] = {};

      for (let day = 0; day < 7; day++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + (week * 7) + day);
        const dateStr = date.toISOString().split('T')[0];

        if (taskAssignments[dateStr]) {
          pattern[week][day] = taskAssignments[dateStr];
        }
      }
    }

    setFourWeekPattern(pattern);
    alert('4-week pattern saved! This pattern will now repeat automatically.');
  };

  // Clear pattern
  const clearPattern = () => {
    if (window.confirm('Clear the saved 4-week pattern and return to default scheduling?')) {
      setFourWeekPattern(null);
      localStorage.removeItem('fourWeekPattern');
    }
  };

  // Apply template
  const applyTemplate = (template) => {
    const newTasks = [...tasks];
    let nextId = Math.max(...tasks.map(t => t.id)) + 1;

    template.tasks.forEach(templateTask => {
      // Check if task already exists
      const exists = tasks.some(t =>
        t.name.toLowerCase() === templateTask.name.toLowerCase()
      );

      if (!exists) {
        newTasks.push({
          id: nextId++,
          ...templateTask,
          personId: 1 // Default to Person A, can be changed later
        });
      }
    });

    setTasks(newTasks);
    setShowTemplates(false);
    alert(`Template "${template.name}" applied! ${template.tasks.length} tasks added.`);
  };

  // Reset all tasks to default schedule
  const resetSchedule = () => {
    if (window.confirm('Reset all tasks to their default schedule? This will clear all manual changes and completions.')) {
      setTaskAssignments({});
      setFourWeekPattern(null);
      localStorage.removeItem('fourWeekPattern');
    }
  };

  // Calendar navigation
  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };
  const getWeekNumber = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
    const week1 = new Date(d.getFullYear(), 0, 4);
    return 1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
  };
  const navigatePrevious = () => {
    const newDate = new Date(currentDate);
    if (view === 'weekly') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };
  const navigateNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'weekly') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const days = [];

    if (view === 'weekly') {
      const start = getStartOfWeek(currentDate);
      for (let i = 0; i < 7; i++) {
        const day = new Date(start);
        day.setDate(start.getDate() + i);
        days.push(day);
      }
    } else {
      const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      const startDay = start.getDay();
      const paddingDays = startDay === 0 ? 6 : startDay - 1;

      for (let i = paddingDays; i > 0; i--) {
        const day = new Date(start);
        day.setDate(start.getDate() - i);
        days.push(day);
      }

      for (let i = 1; i <= end.getDate(); i++) {
        days.push(new Date(currentDate.getFullYear(), currentDate.getMonth(), i));
      }

      while (days.length % 7 !== 0) {
        const lastDay = days[days.length - 1];
        const nextDay = new Date(lastDay);
        nextDay.setDate(lastDay.getDate() + 1);
        days.push(nextDay);
      }
    }

    return days;
  }, [view, currentDate]);
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-800">Task Schedule</h1>

            <div className="flex items-center gap-4">
              {/* Person Legend */}
              <div className="flex items-center gap-3">
                {persons.map(person => (
                  <div key={person.id} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full shadow-sm"
                      style={{ backgroundColor: person.color }}
                    />
                    <span className="text-sm font-medium text-gray-600">{person.name}</span>
                  </div>
                ))}
              </div>

              {/* Template Button */}
              <button
                onClick={() => setShowTemplates(!showTemplates)}
                className="px-3 py-2 bg-blue-100 hover:bg-blue-200 rounded-lg flex items-center gap-2 transition-colors"
                title="Task Templates"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">Templates</span>
              </button>

              {/* Pattern Controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={saveCurrentPattern}
                  className="px-3 py-2 bg-green-100 hover:bg-green-200 rounded-lg flex items-center gap-2 transition-colors"
                  title="Save 4-week pattern"
                >
                  <Save className="w-4 h-4" />
                  <span className="text-sm font-medium">Save Pattern</span>
                </button>
                {fourWeekPattern && (
                  <button
                    onClick={clearPattern}
                    className="px-3 py-2 bg-red-100 hover:bg-red-200 rounded-lg flex items-center gap-2 transition-colors"
                    title="Clear saved pattern"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Reset Button */}
              <button
                onClick={resetSchedule}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 transition-colors"
                title="Reset to default schedule"
              >
                <RotateCcw className="w-4 h-4" />
              </button>

              {/* Filter */}
              <div className="relative">
                <button
                  onClick={() => setShowFilter(!showFilter)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Filter className="w-4 h-4" />
                  <span className="text-sm font-medium">Filter</span>
                </button>

                {showFilter && (
                  <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border p-4 z-20 w-56">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-medium text-gray-700">Filters</h3>
                      <button
                        onClick={() => setShowFilter(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-gray-600 uppercase tracking-wider">Person</label>
                        <select
                          value={filter.person}
                          onChange={(e) => setFilter({ ...filter, person: e.target.value })}
                          className="w-full mt-1 px-3 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="all">All</option>
                          {persons.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="text-xs font-medium text-gray-600 uppercase tracking-wider">Status</label>
                        <select
                          value={filter.status}
                          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                          className="w-full mt-1 px-3 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="all">All</option>
                          <option value="pending">Pending</option>
                          <option value="completed">Completed</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* View Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setView('weekly')}
                  className={`px-3 py-1.5 rounded-md flex items-center gap-2 text-sm font-medium transition-all ${view === 'weekly'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  <List className="w-4 h-4" />
                  Week
                </button>
                <button
                  onClick={() => setView('monthly')}
                  className={`px-3 py-1.5 rounded-md flex items-center gap-2 text-sm font-medium transition-all ${view === 'monthly'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                    }`}
                >
                  <Calendar className="w-4 h-4" />
                  Month
                </button>
              </div>

              {/* Weekly Mode Toggle */}
              {view === 'weekly' && (
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setWeeklyMode('calendar')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${weeklyMode === 'calendar'
                        ? 'bg-white text-purple-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                      }`}
                    title="Calendar View"
                  >
                    📅
                  </button>
                  <button
                    onClick={() => setWeeklyMode('matrix')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${weeklyMode === 'matrix'
                        ? 'bg-white text-purple-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                      }`}
                    title="Matrix View"
                  >
                    📊
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={navigatePrevious}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>

            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-gray-800">
                {view === 'weekly'
                  ? `Week ${getWeekNumber(currentDate)}, ${currentDate.getFullYear()}`
                  : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                }
              </h2>
              {fourWeekPattern && (
                <span className="text-sm px-2 py-1 bg-green-100 text-green-700 rounded-md">
                  Pattern Week {getWeekInCycle(currentDate) + 1}/4
                </span>
              )}
              <button
                onClick={goToToday}
                className="px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                Today
              </button>
            </div>

            <button
              onClick={navigateNext}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Template Modal */}
        {showTemplates && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">Task Templates</h2>
                <button
                  onClick={() => setShowTemplates(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {taskTemplates.map(template => (
                  <div key={template.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <span>{template.emoji}</span>
                          {template.name}
                        </h3>
                        <p className="text-sm text-gray-600">{template.tasks.length} tasks</p>
                      </div>
                      <button
                        onClick={() => applyTemplate(template)}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        Apply
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {template.tasks.map((task, idx) => (
                        <div key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                          <span>{task.emoji}</span>
                          <span>{task.name}</span>
                          <span className="text-xs text-gray-500">
                            ({task.frequency === 'weekly' && task.interval > 1 ? `bi-${task.frequency}` : task.frequency})
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Calendar Grid */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          {view === 'weekly' && weeklyMode === 'matrix' ? (
            // Matrix View for Weekly
            <>
              <div className="grid grid-cols-8 gap-3 mb-3 pb-3 border-b-2 border-gray-200">
                <div className="font-semibold text-gray-700">Task</div>
                {weekDays.map(day => (
                  <div key={day} className="text-center text-sm font-semibold text-gray-600 uppercase tracking-wider">
                    {day}
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                {tasks.map(task => {
                  const person = persons.find(p => p.id === task.personId);
                  const weekDates = calendarDays;

                  const hasTaskThisWeek = weekDates.some(date => {
                    const dayTasks = getTasksForDate(date);
                    return dayTasks.some(t => t.id === task.id);
                  });

                  if (!hasTaskThisWeek) return null;

                  return (
                    <div key={task.id} className="grid grid-cols-8 gap-3 items-center p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{task.emoji}</span>
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: person?.color }}
                        />
                        <span className="text-sm font-medium text-gray-700">{task.name}</span>
                      </div>

                      {weekDates.map((date, index) => {
                        const dateStr = date.toISOString().split('T')[0];
                        const dayTasks = getTasksForDate(date);
                        const taskForDay = dayTasks.find(t => t.id === task.id);
                        const isToday = date.toDateString() === new Date().toDateString();
                        const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));

                        return (
                          <div
                            key={`${task.id}-${dateStr}`}
                            className={`
                              flex justify-center items-center p-2 rounded-lg border-2 transition-all min-h-[40px]
                              ${isToday ? 'border-blue-400 bg-blue-50/30' : 'border-gray-200'}
                              ${isPast ? 'opacity-50' : ''}
                            `}
                            onDragOver={(e) => taskForDay && handleDragOver(e, date)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => taskForDay && handleDrop(e, date)}
                          >
                            {taskForDay && (
                              <div
                                className={`
                                  w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all
                                  ${taskForDay.isCompleted
                                    ? 'bg-gray-300 hover:bg-gray-400'
                                    : 'hover:scale-110'
                                  }
                                `}
                                style={{
                                  backgroundColor: taskForDay.isCompleted ? undefined : person?.color
                                }}
                                onClick={() => toggleTaskCompletion(task.id, dateStr)}
                                draggable={!isPast && !taskForDay.isCompleted}
                                onDragStart={(e) => handleDragStart(e, task, dateStr)}
                                onDragEnd={handleDragEnd}
                                title={`${task.name} - Click to toggle completion`}
                              >
                                {taskForDay.isCompleted && (
                                  <Check className="w-4 h-4 text-white" />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            // Original Calendar View
            <>
              <div className="grid grid-cols-7 gap-3 mb-3">
                {weekDays.map(day => (
                  <div key={day} className="text-center text-sm font-semibold text-gray-600 uppercase tracking-wider py-2">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-3">
                {calendarDays.map((date, index) => {
                  const dateStr = date.toISOString().split('T')[0];
                  const dayTasks = getTasksForDate(date);
                  const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                  const isToday = date.toDateString() === new Date().toDateString();
                  const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
                  const isDragOver = dragOverDate === dateStr;
                  const canDrop = draggedItem && isValidDropTarget(draggedItem.task, date, draggedItem.fromDate);

                  return (
                    <div
                      key={`${dateStr}-${index}`}
                      className={`
                        min-h-[120px] p-3 rounded-lg border-2 transition-all
                        ${isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                        ${isToday ? 'border-blue-400 bg-blue-50/30' : 'border-gray-200'}
                        ${isPast ? 'opacity-50' : ''}
                        ${isDragOver && canDrop ? 'border-green-400 bg-green-50' : ''}
                        ${isDragOver && !canDrop ? 'border-red-400 bg-red-50' : ''}
                      `}
                      onDragOver={(e) => handleDragOver(e, date)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, date)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-bold ${isToday ? 'text-blue-600' : 'text-gray-700'
                          }`}>
                          {date.getDate()}
                        </span>
                        {isToday && (
                          <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                            Today
                          </span>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        {dayTasks.map((task) => (
                          <TaskBadge
                            key={`${task.id}-${dateStr}`}
                            task={task}
                            person={task.person}
                            isCompleted={task.isCompleted}
                            onToggle={() => toggleTaskCompletion(task.id, dateStr)}
                            onDragStart={(e) => handleDragStart(e, task, dateStr)}
                            onDragEnd={handleDragEnd}
                            isDraggable={!isPast && !task.isCompleted}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-100">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span className="text-xl">💡</span> Quick Guide
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
            <div className="flex items-start gap-2">
              <span className="text-purple-500">•</span>
              <span>Click any task to mark it complete</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-purple-500">•</span>
              <span>Drag tasks within their allowed range (weekly tasks stay in week, monthly in month)</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-purple-500">•</span>
              <span>Save your current 4-week layout as a repeating pattern</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-purple-500">•</span>
              <span>Use templates to quickly add common task sets</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-purple-500">•</span>
              <span>Pattern indicator shows which week (1-4) you're in the cycle</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-purple-500">•</span>
              <span>Tasks are color-coded by person with emojis for quick recognition</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default TodoManager;